import { api } from '../slices/api';

export type PendingCutsMethod = 'fastlane' | 'swiss' | 'both';

export interface PendingCutReservation {
	id: string;
	status: 'Not Cut' | 'In Progress';
	cut_length: string;
	saw_kerf: string;
	trim_length: string;
	cut_quantity: number;
	actual_cut_quantity: number;
	pending_quantity: number;
	/** How many of `cut_quantity` are the scrap allowance (already included). */
	scrap_quantity: number;
	reserved_length: string;
	/** Length the remaining pieces take, including kerf. Trim is once per bar. */
	required_length: string;
	saw_priority: number;
	is_swiss: boolean;
	saw_ready: boolean;
	order_line_item: {
		id: string;
		name: string;
		order_name: string | null;
		production_method: string | null;
		due_date: string | null;
	} | null;
	reserved_for_description: string | null;
	reserved_by: string | null;
	reserved_at: string | null;
	flags: string[];
}

export interface PendingCutBar {
	id: string;
	friendly_id: string | null;
	current_length: string;
	location: string | null;
	building: { id: string; name: string } | null;
	status: string;
	deleted: boolean;
	required_length: string;
	remaining_after_cuts: string;
	is_short: boolean;
	pending_pieces: number;
	job_count: number;
	flags: string[];
	reservations: PendingCutReservation[];
}

export interface PendingCutMaterialGroup {
	material_id: string;
	material_name: string;
	pending_pieces: number;
	bars: PendingCutBar[];
	/**
	 * Full material bar count for screen page headers. When a material is
	 * sliced across pages, `bars` is only that page's slice.
	 */
	total_bars?: number;
	/** True when this slice continues a material from an earlier page. */
	continued?: boolean;
}

export interface PendingCutsReport {
	method: PendingCutsMethod;
	summary: {
		reservations: number;
		bars: number;
		pending_pieces: number;
		flagged_bars: number;
		not_saw_ready: number;
	};
	groups: PendingCutMaterialGroup[];
	row_count: number;
	truncated: boolean;
	row_cap: number;
}

export interface PendingCutsReportParams {
	method: PendingCutsMethod;
	building_ids?: string[];
	bar?: string;
}

export const buildPendingCutsQuery = (
	params: PendingCutsReportParams
): string => {
	const query = new URLSearchParams({ method: params.method });
	if (params.building_ids?.length) {
		query.set('building_ids', params.building_ids.join(','));
	}
	if (params.bar?.trim()) query.set('bar', params.bar.trim());
	return query.toString();
};

export const pendingCutsApi = api.injectEndpoints({
	endpoints: (builder) => ({
		getPendingCutsReport: builder.query<
			PendingCutsReport,
			PendingCutsReportParams
		>({
			query: (params) =>
				`material/reports/pending-cuts/?${buildPendingCutsQuery(params)}`,
		}),
	}),
});

export const { useGetPendingCutsReportQuery } = pendingCutsApi;
